import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useLanguage } from '../../../contexts/LanguageContext';

const DiaryCreateForm = ({ sites = [], submittedByName = '', onSubmit, loading = false, readOnly = false }) => {
  const { t } = useLanguage();
  const today = new Date()?.toISOString()?.split('T')?.[0];
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const WEATHER_OPTIONS = [
    { value: '', label: t('selectWeather') },
    { value: 'Sunny', label: '☀️ ' + t('sunny') },
    { value: 'Cloudy', label: '☁️ ' + t('cloudy') },
    { value: 'Rainy', label: '🌧️ ' + t('rainy') },
    { value: 'Windy', label: '💨 ' + t('windy') },
    { value: 'Overcast', label: '🌥️ ' + t('overcast') },
  ];

  const [form, setForm] = useState({
    siteId: '',
    date: today,
    weather: '',
    labourOnSite: '',
    deliveries: '',
    workCompleted: '',
    issuesDelays: '',
    generalNotes: '',
  });
  const [photos, setPhotos] = useState([]);
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form?.siteId) errs.siteId = t('selectSiteRequired');
    if (!form?.date) errs.date = t('selectDateRequired');
    if (!form?.weather) errs.weather = t('selectWeatherRequired');
    return errs;
  };

  const handlePhotoFiles = (files) => {
    const valid = Array.from(files)?.filter(f => f?.type?.startsWith('image/'));
    setPhotos(prev => [
      ...prev,
      ...valid?.map(f => ({ file: f, preview: URL.createObjectURL(f) }))
    ]);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragOver(false);
    handlePhotoFiles(e?.dataTransfer?.files);
  };

  const removePhoto = (idx) => {
    setPhotos(prev => prev?.filter((_, i) => i !== idx));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    const errs = validate();
    if (Object.keys(errs)?.length) { setErrors(errs); return; }
    onSubmit?.(form, photos?.map(p => p?.file));
  };

  const labelClass = 'block text-sm font-medium text-foreground mb-1';
  const inputClass = 'w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent transition-colors';
  const textareaClass = `${inputClass} resize-none`;
  const errorClass = 'text-xs text-destructive mt-1';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Row 1: Site + Date */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>{t('site')} <span className="text-destructive">*</span></label>
          <select
            value={form?.siteId}
            onChange={e => handleChange('siteId', e?.target?.value)}
            className={inputClass}
          >
            <option value="">{t('selectSitePlaceholder')}</option>
            {sites?.map(s => (
              <option key={s?.id} value={s?.id}>{s?.site_name}</option>
            ))}
          </select>
          {errors?.siteId && <p className={errorClass}>{errors?.siteId}</p>}
        </div>
        <div>
          <label className={labelClass}>{t('date')} <span className="text-destructive">*</span></label>
          <input
            type="date"
            value={form?.date}
            onChange={e => handleChange('date', e?.target?.value)}
            className={inputClass}
          />
          {errors?.date && <p className={errorClass}>{errors?.date}</p>}
        </div>
      </div>
      {/* Row 2: Weather + Submitted By */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>{t('weatherConditions')} <span className="text-destructive">*</span></label>
          <select
            value={form?.weather}
            onChange={e => handleChange('weather', e?.target?.value)}
            className={inputClass}
          >
            {WEATHER_OPTIONS?.map(opt => (
              <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
            ))}
          </select>
          {errors?.weather && <p className={errorClass}>{errors?.weather}</p>}
        </div>
        <div>
          <label className={labelClass}>{t('submittedBy')}</label>
          <input
            type="text"
            value={submittedByName}
            readOnly
            className={`${inputClass} bg-muted/50 text-muted-foreground cursor-not-allowed`}
          />
        </div>
      </div>
      {/* Labour on Site */}
      <div>
        <label className={labelClass}>{t('labourOnSite')}</label>
        <textarea
          rows={3}
          placeholder={t('labourOnSitePlaceholder')}
          value={form?.labourOnSite}
          onChange={e => handleChange('labourOnSite', e?.target?.value)}
          className={textareaClass}
        />
      </div>
      {/* Deliveries */}
      <div>
        <label className={labelClass}>{t('deliveries')}</label>
        <textarea
          rows={3}
          placeholder={t('deliveriesPlaceholder')}
          value={form?.deliveries}
          onChange={e => handleChange('deliveries', e?.target?.value)}
          className={textareaClass}
        />
      </div>
      {/* Work Completed */}
      <div>
        <label className={labelClass}>{t('workCompleted')}</label>
        <textarea
          rows={4}
          placeholder={t('workCompletedPlaceholder')}
          value={form?.workCompleted}
          onChange={e => handleChange('workCompleted', e?.target?.value)}
          className={textareaClass}
        />
      </div>
      {/* Issues / Delays */}
      <div>
        <label className={labelClass}>{t('issuesDelays')}</label>
        <textarea
          rows={3}
          placeholder={t('issuesDelaysPlaceholder')}
          value={form?.issuesDelays}
          onChange={e => handleChange('issuesDelays', e?.target?.value)}
          className={textareaClass}
        />
      </div>
      {/* General Notes */}
      <div>
        <label className={labelClass}>{t('generalNotes')}</label>
        <textarea
          rows={3}
          placeholder={t('generalNotesPlaceholder')}
          value={form?.generalNotes}
          onChange={e => handleChange('generalNotes', e?.target?.value)}
          className={textareaClass}
        />
      </div>
      {/* Photo Upload */}
      <div>
        <label className={labelClass}>{t('photos')}</label>
        <div
          onDragOver={e => { e?.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef?.current?.click()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/50 hover:bg-muted/30'
          }`}
        >
          <Icon name="Upload" size={28} className="mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">{t('dropPhotosOrClick')}</p>
          <p className="text-xs text-muted-foreground mt-1">{t('photoUploadFormats')}</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={e => handlePhotoFiles(e?.target?.files)}
          />
        </div>

        {photos?.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 mt-3">
            {photos?.map((p, idx) => (
              <div key={idx} className="relative group">
                <img
                  src={p?.preview}
                  alt={`Upload preview ${idx + 1}`}
                  className="w-full h-20 object-cover rounded-lg border border-border"
                />
                <button
                  type="button"
                  onClick={() => removePhoto(idx)}
                  className="absolute top-1 right-1 bg-destructive text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Icon name="X" size={10} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Submit */}
      <div className="flex justify-end pt-2">
        <Button type="submit" variant="default" size="lg" disabled={loading || readOnly}>
          {loading ? (
            <><Icon name="Loader2" size={16} className="animate-spin" /> {t('saving')}</>
          ) : (
            <><Icon name="Save" size={16} /> {t('saveDiaryEntry')}</>
          )}
        </Button>
      </div>
    </form>
  );
};

export default DiaryCreateForm;
